// apps/web/src/components/ReacceptanceModal/ReacceptanceModal.tsx
//
// Phase 01 onboarding-completion (W3.8) — blocking re-acceptance modal.
//
// Renders on top of every authenticated screen when `useAuth().user.pending_reacceptance`
// is non-null. Two paths out:
//   1. Accept the new version  → useReacceptLegalText → /auth/me refetches → modal unmounts
//   2. Snooze for 24h           → useSnoozeReacceptance → modal closes locally until next session
//
// Pure-overlay component — no route registration needed. AppLayout renders it
// once at the layout level so it appears regardless of the active page.

import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useReacceptLegalText, useSnoozeReacceptance } from '@alamo/api';
import { useAuth } from '@alamo/auth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@alamo/ui';

export function ReacceptanceModal(): JSX.Element | null {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const reaccept = useReacceptLegalText();
  const snooze = useSnoozeReacceptance();

  // Local dismiss flag — covers the "snooze" path within the current tab.
  // Server-side dismissed_until handles the cross-tab/cross-session case.
  const [dismissedLocally, setDismissedLocally] = useState(false);

  const pending = user?.pending_reacceptance ?? null;
  if (!pending || dismissedLocally) return null;

  const handleAccept = async () => {
    await reaccept.mutateAsync({ textId: pending.textId });
    await refreshUser();
  };

  const handleSnooze = async () => {
    const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    await snooze.mutateAsync({ textId: pending.textId, until });
    setDismissedLocally(true);
  };

  // AlertDialog is the right primitive: blocking by design (no backdrop
  // dismiss, ESC blocked by default per Radix), focus traps on actions.
  return (
    <AlertDialog open>
      <AlertDialogContent className="w-[calc(100%-2rem)] max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('common:reacceptance.title', {
              defaultValue: 'Termos atualizados — releia e aceite',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('common:reacceptance.description', {
              defaultValue:
                'Publicamos uma nova versão dos termos de uso ({{version}}). Para continuar usando a plataforma você precisa lê-los e aceitá-los abaixo.',
              version: pending.version,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-4 max-h-[50vh] overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <h2 className="mb-3 font-heading text-base font-semibold text-secondary-950">
            {pending.title}
          </h2>
          {/* Content is HTML rendered from admin — trusted source (admin role). */}
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: pending.content }}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={(e) => {
              e.preventDefault();
              void handleSnooze();
            }}
            disabled={snooze.isPending || reaccept.isPending}
          >
            <Button variant="outline" isLoading={snooze.isPending}>
              {t('common:reacceptance.snooze', {
                defaultValue: 'Adiar 24 horas',
              })}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleAccept();
            }}
            disabled={reaccept.isPending || snooze.isPending}
          >
            <Button variant="primary" isLoading={reaccept.isPending}>
              {reaccept.isPending
                ? t('common:loading', { defaultValue: 'Carregando…' })
                : t('common:reacceptance.accept', {
                    defaultValue: 'Aceitar e continuar',
                  })}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
